import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  onAuthStateChanged,
  signInWithPopup,
  signInWithCustomToken,
  GoogleAuthProvider,
  OAuthProvider,
  getAdditionalUserInfo,
  User as FirebaseUser,
  UserCredential,
} from 'firebase/auth';
import { doc, getDoc, onSnapshot, updateDoc, serverTimestamp, deleteField } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { migrateNotificationToken } from '../utils/migrateNotificationToken';
import { auth, db, functions } from '../firebase/config';
import { UserData, AuthState, DeletionRequestStatus } from '../types/user';
import { offlineAuthService } from '../services/offlineAuthService';
import { userService } from '../services/userService';
import { notificationService } from '../services/notificationService';
import { analyticsService } from '../services/analyticsService';
import { safeGetItem, safeRemoveItem } from '../utils/localStorageUtils';

// "ready" is intentionally not blocked here — PrivateRoute lets that user
// in and routes them to /complete-account-removal instead.
function deletionLoginBlockMessage(status: DeletionRequestStatus | undefined): string | null {
  if (status === 'pending') {
    return 'We sent you a confirmation email for your account deletion request. Please confirm it, or contact support to cancel.';
  }
  if (status === 'confirmed') {
    return "Your account deletion request is confirmed and scheduled. We'll email you when it's ready to complete.";
  }
  return null;
}

// Lets the UI distinguish "blocked by pending/confirmed deletion" (persistent
// banner, no retry) from ordinary auth errors (toast) without re-deriving the
// message text in two places.
export function isDeletionBlockedMessage(message: string | null | undefined): boolean {
  if (!message) return false;
  return (
    message === deletionLoginBlockMessage('pending') ||
    message === deletionLoginBlockMessage('confirmed')
  );
}

export interface PendingTwoFactor {
  uid: string;
  // All methods the user has configured — if length > 1, show picker
  availableMethods: Array<'email' | 'authenticator'>;
  // The method currently being challenged (set after picker or auto-selected)
  method: 'email' | 'authenticator' | null;
}

/**
 * Result of a social sign-in from the onboarding "Let's begin" step.
 * `isNewUser` comes from Firebase's additionalUserInfo, not the undocumented
 * `_tokenResponse`. `undefined` result means a 2FA challenge was raised.
 */
export interface ProviderSignInResult {
  user: FirebaseUser;
  isNewUser: boolean;
  /** Given name from the provider profile, if any. */
  firstName: string;
}

/** Normalises a popup UserCredential (Google or Apple) for the onboarding wizard. */
export const describeProviderResult = (result: UserCredential): ProviderSignInResult => {
  const info = getAdditionalUserInfo(result);
  const profile = (info?.profile ?? {}) as Record<string, any>;
  const firstName =
    profile.given_name ||
    profile.name?.firstName ||
    (result.user.displayName ?? '').split(' ')[0] ||
    '';
  return { user: result.user, isNewUser: Boolean(info?.isNewUser), firstName };
};

interface AuthContextProps {
  currentUser: FirebaseUser | null;
  userData: UserData | null;
  login: (email: string, password: string) => Promise<void>;
  /**
   * Creates the Firebase Auth account only. No `users/{uid}` document is
   * written — the onboarding wizard writes the profile at its final step
   * (same order as iOS `OnboardingController.createAccount` → `finish`).
   */
  createAccountWithEmail: (email: string, password: string) => Promise<FirebaseUser>;
  /**
   * Legacy helper: Auth account + profile documents in one go. Kept for
   * CompleteProfile-style flows; the onboarding wizard does not use it.
   */
  signup: (email: string, password: string, userData: UserData) => Promise<FirebaseUser>;
  continueWithGoogle: () => Promise<any>;
  /** Sign in with Apple via OAuthProvider('apple.com') popup. */
  continueWithApple: () => Promise<ProviderSignInResult | undefined>;
  logout: () => Promise<void>;
  refreshUserData: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  completeTwoFactorLogin: (code: string, method: 'email' | 'authenticator') => Promise<void>;
  cancelTwoFactorLogin: () => void;
  selectTwoFactorMethod: (method: 'email' | 'authenticator') => void;
  pendingTwoFactor: PendingTwoFactor | null;
  authState: AuthState;
  isOfflineMode: boolean;
  // True only until the very first session check resolves. Distinct from
  // authState.loading, which also flips true/false on every subsequent
  // login/signup/logout action — callers that gate a full-screen loader on
  // "are we still figuring out if the user is logged in" should use this,
  // not authState.loading, or they'll unmount the current page (wiping form
  // state) on every login attempt.
  initializing: boolean;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [isOfflineMode, setIsOfflineMode] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [pendingTwoFactor, setPendingTwoFactor] = useState<PendingTwoFactor | null>(null);
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    loading: true,
    error: null
  });

  const pendingTwoFactorRef = useRef<PendingTwoFactor | null>(null);
  pendingTwoFactorRef.current = pendingTwoFactor;

  // Carries the deletion-block message from the listener's authenticated
  // branch (where it's computed) across the signOut() it triggers, to the
  // listener's signed-out branch (where authState is finally settled). This
  // is what makes the message survive as a persistent banner instead of
  // depending on a component that may unmount mid-race.
  const deletionBlockMessageRef = useRef<string | null>(null);

  // Sign out if page is closed/refreshed mid-2FA challenge
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (pendingTwoFactorRef.current) {
        signOut(auth).catch(() => {});
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // FCM token refresh after SW update
  useEffect(() => {
    const flag = safeGetItem('refreshFCMTokenAfterSW');
    if (flag === 'true' && currentUser?.uid) {
      safeRemoveItem('refreshFCMTokenAfterSW');
      notificationService.addFCMTokenToUser(currentUser.uid, true).catch(() => {});
    }
  }, [currentUser]);

  // Online / offline
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleOnline = () => setIsOfflineMode(false);
    const handleOffline = () => {
      setIsOfflineMode(true);
      const cached = offlineAuthService.getCachedAuthState();
      if (cached) {
        setUserData(cached.userData);
        setAuthState({ isAuthenticated: cached.isAuthenticated, user: cached.userData, loading: false, error: null });
      }
      setInitializing(false);
    };
    setIsOfflineMode(typeof navigator !== 'undefined' ? !navigator.onLine : false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Offline init
  useEffect(() => {
    if (typeof navigator === 'undefined' || navigator.onLine) return;
    const cached = offlineAuthService.getCachedAuthState();
    if (cached) {
      setUserData(cached.userData);
      setAuthState({ isAuthenticated: cached.isAuthenticated, user: cached.userData, loading: false, error: null });
      setIsOfflineMode(true);
    } else {
      setAuthState({ isAuthenticated: false, user: null, loading: false, error: null });
    }
    setInitializing(false);
  }, []);

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const sendPasswordReset = async (email: string) => {
    if (!email) throw new Error('Email is required');
    await sendPasswordResetEmail(auth, email, {
      handleCodeInApp: true,
      url: window.location.origin + '/reset-password',
    });
  };

  /**
   * Reads 2FA status for a uid while the user IS authenticated.
   * Returns available configured methods, or null if 2FA is disabled.
   */
  const readTwoFactorStatus = async (uid: string): Promise<PendingTwoFactor | null> => {
    try {
      const snap = await getDoc(doc(db, 'users', uid));
      if (!snap.exists()) return null;
      const d = snap.data();
      if (!d?.twoFactorEnabled) return null;

      // New schema: twoFactorMethods: { email: boolean, authenticator: boolean }
      const methods: Array<'email' | 'authenticator'> = [];
      if (d.twoFactorMethods?.email) methods.push('email');
      if (d.twoFactorMethods?.authenticator) methods.push('authenticator');

      // Legacy fallback: single twoFactorMethod field
      if (methods.length === 0 && d.twoFactorMethod) {
        methods.push(d.twoFactorMethod as 'email' | 'authenticator');
      }

      if (methods.length === 0) return null;

      return {
        uid,
        availableMethods: methods,
        // Auto-select if only one method; otherwise user picks in UI
        method: methods.length === 1 ? methods[0] : null,
      };
    } catch {
      return null;
    }
  };

  // ── Auth state listener ───────────────────────────────────────────────────────
  useEffect(() => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log(`[AUTH_CONTEXT] onAuthStateChanged:`, user?.email ?? 'null');

      setCurrentUser(user);

      if (user) {
        // If we have a pending 2FA challenge, don't process the auth state change —
        // the session is intentionally kept alive while the user completes 2FA.
        if (pendingTwoFactorRef.current) {
          console.log('[AUTH_CONTEXT] onAuthStateChanged skipped — 2FA pending');
          return;
        }

        setAuthState(prev => ({ ...prev, loading: true }));
        try {
          const data = await userService.getUserData(user.uid);
          if (data) {
            // Authoritative deletion gate — covers every entry point (login,
            // Google sign-in, an already-open tab, a reload with a persisted
            // session, a second device) since they all funnel through this
            // listener. Blocked users never see isAuthenticated: true.
            const blockedMessage = deletionLoginBlockMessage(data.deletionStatus);
            if (blockedMessage) {
              deletionBlockMessageRef.current = blockedMessage;
              await signOut(auth);
              // The signOut above re-invokes this listener with user: null,
              // which settles authState using the ref. Nothing left to do here.
              return;
            }

            offlineAuthService.cacheAuthState(user.uid, data);
            analyticsService.setUser(user.uid);
            analyticsService.setUserProperties({
              role: data.role,
              onboarding_complete: data.onboardingComplete,
            });
            setUserData(data);
            setAuthState({ isAuthenticated: true, user: data, loading: false, error: null });

            // Record this device as an active session (used by the "Active
            // Devices" security settings list). Best-effort — never block auth.
            try {
              const deviceInfo = notificationService.getDeviceInfo();
              const sessionPath = `activeSessions.${deviceInfo.deviceId}`;
              await updateDoc(doc(db, 'users', user.uid, 'private', 'data'), {
                [`${sessionPath}.deviceName`]: deviceInfo.deviceName,
                [`${sessionPath}.platform`]: deviceInfo.platform,
                [`${sessionPath}.lastActive`]: serverTimestamp(),
              });
            } catch (sessionError) {
              console.error('[AUTH_CONTEXT] Failed to record active session:', sessionError);
            }
          } else {
            setAuthState({ isAuthenticated: true, user: null, loading: false, error: 'User data not found' });
          }
        } catch (error) {
          if (typeof navigator !== 'undefined' && !navigator.onLine) {
            const cached = offlineAuthService.getCachedAuthState();
            if (cached) {
              setUserData(cached.userData);
              setAuthState({ isAuthenticated: cached.isAuthenticated, user: cached.userData, loading: false, error: null });
              setInitializing(false);
              return;
            }
          }
          setAuthState({ isAuthenticated: false, user: null, loading: false, error: (error as Error).message });
        }
        setInitializing(false);
      } else {
        setUserData(null);
        if (typeof navigator !== 'undefined' && navigator.onLine) {
          offlineAuthService.clearAuthState();
        }
        // Only reset auth state if NOT in a 2FA challenge.
        // During a 2FA challenge we sign the user out temporarily (Google flow) or keep them
        // signed in (email flow) — either way, the UI is controlled by pendingTwoFactor.
        if (!pendingTwoFactorRef.current) {
          const blockedMessage = deletionBlockMessageRef.current;
          deletionBlockMessageRef.current = null;
          setAuthState({ isAuthenticated: false, user: null, loading: false, error: blockedMessage });
        }
        setInitializing(false);
      }
    });

    return unsubscribe;
  }, []);

  // Force sign-out on this device if the password was changed elsewhere.
  // passwordChangedAt/passwordChangedByDeviceId are only ever set by the
  // revokeOtherSessions Cloud Function, right after a successful email/password
  // change — Google-auth users never trigger this, so the listener is a no-op
  // for them. lastSignInTime stays fixed for the life of a session (it's only
  // reissued on a fresh sign-in), so comparing it against passwordChangedAt
  // correctly identifies sessions that predate the change, on both an already
  // open tab (via this live listener) and on reload (first snapshot already
  // reflects the field).
  useEffect(() => {
    if (!currentUser) return;

    const ref = doc(db, 'users', currentUser.uid, 'private', 'data');
    const unsubscribe = onSnapshot(ref, (snap) => {
      const changedAt = snap.data()?.passwordChangedAt?.toMillis?.();
      const changedByDeviceId = snap.data()?.passwordChangedByDeviceId;
      if (!changedAt) return;

      const sessionStart = new Date(currentUser.metadata.lastSignInTime || 0).getTime();
      if (changedAt > sessionStart && changedByDeviceId !== notificationService.getCurrentDeviceId()) {
        console.log('[AUTH_CONTEXT] Password changed on another device — signing out');
        signOut(auth).catch(() => {});
      }
    });

    return unsubscribe;
  }, [currentUser]);

 const login = async (email: string, password: string) => {
  try {
    setAuthState(prev => ({ ...prev, loading: true, error: null }));

    const credential = await signInWithEmailAndPassword(auth, email, password);

    // Deletion gate lives in the onAuthStateChanged listener now — it's the
    // only place that can be authoritative across every entry point (this
    // login, Google sign-in, reload, another tab). Checking it here too used
    // to race the listener and lose.
    const twoFa = await readTwoFactorStatus(credential.user.uid);

    if (twoFa) {
      setPendingTwoFactor(twoFa);
      pendingTwoFactorRef.current = twoFa;
      setAuthState({ isAuthenticated: false, user: null, loading: false, error: null });
      return;
    }

    analyticsService.trackAuth('login', 'email');
  } catch (error) {
    setAuthState(prev => ({ ...prev, loading: false, error: (error as Error).message }));
    throw error;
  }
};

const continueWithGoogle = async (): Promise<any> => {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    throw new Error('Google sign in requires an internet connection');
  }
  try {
    setAuthState(prev => ({ ...prev, loading: true, error: null }));
    const provider = new GoogleAuthProvider();
    provider.addScope('email');
    provider.addScope('profile');
    const result = await signInWithPopup(auth, provider);

    // Deletion gate lives in the onAuthStateChanged listener now — see login().
    const twoFa = await readTwoFactorStatus(result.user.uid);
    if (twoFa) {
      pendingTwoFactorRef.current = twoFa;
      setPendingTwoFactor(twoFa);
      setAuthState({ isAuthenticated: false, user: null, loading: false, error: null });
      return;
    }

    analyticsService.trackAuth('login', 'google');
    return result;
  } catch (error) {
    setAuthState(prev => ({ ...prev, loading: false, error: (error as Error).message }));
    throw error;
  }
};

const continueWithApple = async (): Promise<ProviderSignInResult | undefined> => {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    throw new Error('Apple sign in requires an internet connection');
  }
  try {
    setAuthState(prev => ({ ...prev, loading: true, error: null }));
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    const result = await signInWithPopup(auth, provider);

    // Deletion gate lives in the onAuthStateChanged listener — see login().
    const twoFa = await readTwoFactorStatus(result.user.uid);
    if (twoFa) {
      pendingTwoFactorRef.current = twoFa;
      setPendingTwoFactor(twoFa);
      setAuthState({ isAuthenticated: false, user: null, loading: false, error: null });
      return undefined;
    }

    analyticsService.trackAuth('login', 'apple');
    return describeProviderResult(result);
  } catch (error) {
    setAuthState(prev => ({ ...prev, loading: false, error: (error as Error).message }));
    throw error;
  }
};

  // ── Method picker (when user has both methods configured) ─────────────────────
  const selectTwoFactorMethod = (method: 'email' | 'authenticator') => {
    if (!pendingTwoFactor) return;
    const updated = { ...pendingTwoFactor, method };
    setPendingTwoFactor(updated);
    pendingTwoFactorRef.current = updated;
  };

  // ── completeTwoFactorLogin ────────────────────────────────────────────────────
  const completeTwoFactorLogin = async (code: string, method: 'email' | 'authenticator') => {
    if (!pendingTwoFactor) throw new Error('No pending 2FA session.');
    try {
      const verifyFn = httpsCallable(functions, 'verifyTwoFactorLogin');
      const result: any = await verifyFn({ code, method, uid: pendingTwoFactor.uid });
      const { customToken } = result.data;

      // Clear pending before signInWithCustomToken so the onAuthStateChanged
      // listener processes the new auth state normally.
      setPendingTwoFactor(null);
      pendingTwoFactorRef.current = null;

      await signInWithCustomToken(auth, customToken);
      window.location.reload();
    } catch (error) {
      throw error;
    }
  };

  const cancelTwoFactorLogin = () => {
    signOut(auth).catch(() => {});
    setPendingTwoFactor(null);
    pendingTwoFactorRef.current = null;
    setAuthState({ isAuthenticated: false, user: null, loading: false, error: null });
  };

  // ── createAccountWithEmail (Auth only) ────────────────────────────────────────
  const createAccountWithEmail = async (email: string, password: string): Promise<FirebaseUser> => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) throw new Error('Signup requires an internet connection');
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      const credential = await createUserWithEmailAndPassword(auth, email, password);
      // The listener settles authState as "authenticated, no profile yet";
      // the wizard treats that as "resume onboarding", not an error.
      analyticsService.trackAuth('signup', 'email');
      analyticsService.setUser(credential.user.uid);
      return credential.user;
    } catch (error) {
      setAuthState(prev => ({ ...prev, loading: false, error: (error as Error).message }));
      throw error;
    }
  };

  // ── signup (Auth + profile docs, legacy) ──────────────────────────────────────
  const signup = async (email: string, password: string, userData: UserData): Promise<FirebaseUser> => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) throw new Error('Signup requires an internet connection');
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      const credential = await createUserWithEmailAndPassword(auth, email, password);
      await userService.createUserDocument(credential.user.uid, userData);
      offlineAuthService.cacheAuthState(credential.user.uid, userData);
      analyticsService.trackAuth('signup', 'email');
      analyticsService.setUser(credential.user.uid);
      analyticsService.setUserProperties({ role: userData.role });
      setAuthState({ isAuthenticated: true, user: userData, loading: false, error: null });
      migrateNotificationToken(credential.user.uid).catch(() => {});
      return credential.user;
    } catch (error) {
      setAuthState(prev => ({ ...prev, loading: false, error: (error as Error).message }));
      throw error;
    }
  };

  // ── refreshUserData ───────────────────────────────────────────────────────────
  const refreshUserData = async () => {
    if (!currentUser) return;
    try {
      const data = await userService.getUserData(currentUser.uid);
      if (data) {
        offlineAuthService.cacheAuthState(currentUser.uid, data);
        setUserData(data);
        setAuthState(prev => ({ ...prev, user: data }));
      }
    } catch (error) {
      console.error('[AUTH_CONTEXT] Error refreshing user data:', error);
    }
  };

  // ── logout ────────────────────────────────────────────────────────────────────
  const logout = async () => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      offlineAuthService.clearAuthState();
      analyticsService.trackAuth('logout');
      analyticsService.setUser(null);
      if (typeof navigator !== 'undefined' && navigator.onLine) {
        const uid = auth.currentUser?.uid;
        if (uid) {
          try {
            const deviceId = notificationService.getCurrentDeviceId();
            await updateDoc(doc(db, 'users', uid, 'private', 'data'), {
              [`activeSessions.${deviceId}`]: deleteField(),
            });
          } catch (sessionError) {
            console.error('[AUTH_CONTEXT] Failed to remove active session on logout:', sessionError);
          }
        }
        await signOut(auth);
      }
      setCurrentUser(null);
      setUserData(null);
      setPendingTwoFactor(null);
      pendingTwoFactorRef.current = null;
      setAuthState({ isAuthenticated: false, user: null, loading: false, error: null });
    } catch (error) {
      setAuthState(prev => ({ ...prev, loading: false, error: (error as Error).message }));
      throw error;
    }
  };

  const value: AuthContextProps = {
    currentUser, userData, login, signup, createAccountWithEmail, sendPasswordReset,
    continueWithGoogle, continueWithApple, logout, refreshUserData,
    completeTwoFactorLogin, cancelTwoFactorLogin, selectTwoFactorMethod,
    pendingTwoFactor, authState, isOfflineMode, initializing,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;